import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * In-cart upsell ("Oszczędzaj więcej", snippets/sauka-cart-upsell.liquid).
 *
 * The markup is a real <form action="/cart/add">, so without JS the button
 * still adds the product (Shopify then redirects to /cart). With JS we add it
 * the same way product-form.js does: request the cart-items sections along
 * with the add, then dispatch CartLinesUpdateEvent — cart-items-component
 * morphs the drawer / cart page from those sections and the cart bubble
 * updates, so the upsell disappears on its own once the cart has 2 jars.
 */
class SaukaCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('click', this.#onClick);
    this.#track = this.querySelector('.sauka-upsell__track');
    this.#track?.addEventListener('scroll', this.#syncArrows, { passive: true });
    this.#syncArrows();
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('click', this.#onClick);
    this.#track?.removeEventListener('scroll', this.#syncArrows);
  }

  /** @type {HTMLElement | null} */
  #track = null;

  #atEnd() {
    const track = this.#track;
    if (!track) return false;
    const max = track.scrollWidth - track.clientWidth;
    // max is 0 while the drawer is closed (nothing laid out) — that is the
    // start, not the end, or the arrow would load already turned around
    return max > 2 && track.scrollLeft >= max - 2;
  }

  /**
   * The → button is a toggle: at the start of the track it points right and
   * scrolls to the next card; at the end it is turned 180° (CSS, [data-back])
   * and scrolls back to the first. Synced on every scroll, so a manual swipe
   * flips it too.
   */
  #syncArrows = () => {
    const back = this.#atEnd();
    this.querySelectorAll('[data-sauka-upsell-next]').forEach((button) => {
      button.toggleAttribute('data-back', back);
      button.setAttribute('aria-label', button.dataset[back ? 'labelBack' : 'labelNext'] || '');
    });
  };

  #onClick = (event) => {
    const button = event.target instanceof Element && event.target.closest('[data-sauka-upsell-next]');
    const track = this.#track;
    if (!button || !track) return;

    let left = 0;
    if (!this.#atEnd()) {
      const target = button.closest('.sauka-upsell__card')?.nextElementSibling;
      if (!(target instanceof HTMLElement)) return;
      // measured against the track (offsetLeft would be relative to whatever
      // positioned ancestor happens to exist)
      const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      left = track.scrollLeft + target.getBoundingClientRect().left - track.getBoundingClientRect().left - pad;
    }

    const before = track.scrollLeft;
    track.scrollTo({ left, behavior: 'smooth' });
    // older Safari ignores scroll options and embedded webviews don't animate
    setTimeout(() => {
      if (track.scrollLeft === before) track.scrollLeft = left;
      this.#syncArrows();
    }, 250);
  };

  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const button = form.querySelector('[data-sauka-upsell-add]');
    if (button?.getAttribute('aria-disabled') === 'true') return;
    button?.setAttribute('aria-disabled', 'true');

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((component) => component instanceof HTMLElement && component.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      }).then((res) => res.json());

      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'sauka-cart-upsell',
          sections: response.status ? undefined : response.sections,
          didError: Boolean(response.status),
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
    } finally {
      // on success the section morph replaces this element anyway
      button?.removeAttribute('aria-disabled');
    }
  };
}

if (!customElements.get('sauka-cart-upsell')) {
  customElements.define('sauka-cart-upsell', SaukaCartUpsell);
}
